<template>
  <main>
    <b-loading :is-full-page="true" v-model="loading"></b-loading>
    <alert-modal
      v-if="isSaveNetworkModalOpen"
      title="Are you sure you want to exclude the pre-enrollment site?"
      text="There will be no PE site for this client, as none of the networks are selected to be included."
      primaryBtnText="Save"
      secondaryBtnText="Cancel"
      @primaryBtnClick="createOrUpdateClient"
      @secondaryBtnClick="isSaveNetworkModalOpen = false"
      @close="isSaveNetworkModalOpen = false"
    />
    <alert-modal
      v-if="isSaveModalOpen"
      title="Save your work without publishing the pre-enrollment site"
      text="The configurations is completed but you have opted not to publish. You can come back at a later date to publish."
      primaryBtnText="Not yet, just save my work"
      secondaryBtnText="Publish pre-enrollment site"
      dismissible
      @primaryBtnClick="createOrUpdateClient"
      @secondaryBtnClick="submitPreEnrollment"
      @close="isSaveModalOpen = false"
    />
    <alert-modal
      v-if="isSavePlanAffiliationModalOpen"
      title="One or more plans are not associated with a network"
      text="Provider and network data will not be available for these plans"
      primaryBtnText="Save anyway"
      secondaryBtnText="Keep working"
      dismissible
      @primaryBtnClick="onAffiliationModalConfirm"
      @secondaryBtnClick="onKeepWorking"
      @close="isSavePlanAffiliationModalOpen = false"
    />
    <alert-modal
      v-if="getShowNetworkAlertModal"
      title="Unsaved work!"
      text="Your work has not been saved and will be lost."
      secondaryBtnText="Continue working"
      primaryBtnText="Leave this page"
      @primaryBtnClick="goHome"
      @secondaryBtnClick="keepChanges"
    />
    <div class="config--save-container">
      <alert
        v-if="saveError && (preEnrollmentInvalid || networksInvalid)"
        type="error"
        class="save--alert"
      >
        <span v-if="networksInvalid && preEnrollmentInvalid"
          >Please fix all errors, and try saving again.</span
        >
        <span v-if="networksInvalid && !preEnrollmentInvalid">
          Please fix the errors in the
          <span class="error--bold"> Networks </span>
          tab, and try saving again.
        </span>
        <span v-if="preEnrollmentInvalid && !networksInvalid">
          Please fix the errors in the
          <span class="error--bold"> Pre-enrollment </span>
          tab, and try saving again.
        </span>
      </alert>
      <alert v-if="unexpectedSaveError" type="error" class="save--alert">
        An unexpected error has occurred while saving.
      </alert>
      <alert v-if="savedSuccess" type="success" class="save--alert">
        You have successfully submitted your work.
      </alert>

      <b-button class="save" @click="onClickSave" :disabled="!saveEnabled"
        >Save</b-button
      >
    </div>

    <b-tabs class="network--tabs" v-model="activeTab">
      <b-tab-item label="Networks">
        <client-form
          v-model="draft"
          @setSaveEnabled="setSaveEnabled"
          @hideValidationErrors="hideValidationErrors"
          @wipePlanAffiliation="handleWipePlanAffiliation"
          :initialName="initialName"
          :showDeepValidationErrors="showDeepValidationErrors"
          :clientConfigNetworkUuids="clientConfigNetworkUuids"
          :planAffiliationNetworkUuids="planAffiliationNetworkUuids"
        />
      </b-tab-item>

      <b-tab-item label="Pre-enrollment" :disabled="!preEnrollmentEnabled">
        <b-tooltip
          slot="header"
          label="This tab is not available based on your network configurations. This tab will be available if at least one network is configured to be included on the OE site."
          :animated="false"
          :triggers="!preEnrollmentEnabled ? undefined : []"
        >
          <span>Pre-enrollment</span>
        </b-tooltip>
        <pre-enrollment-form
          :client-data="clientData"
          v-model="draft.preEnrollment"
          :handleSubmit="submitPreEnrollment"
          @setPreEnrollmentInvalid="setPreEnrollmentInvalid"
        />
      </b-tab-item>
      <b-tab-item
        label="Plan affiliation"
        :disabled="!enabledPlanAffiliationTab"
      >
        <PlanAffiliationsForm
          v-if="networkNames"
          :key="reloadPlanAffilication"
          :planAffiliation="planAffiliation"
          @plan-type-change="onPlanAffiliationPlanTypeChange"
          @client-id-change="onPlanAffiliationClientIdChange"
          @network-affiliation-change="onPlanAffiliationNetworkAffiliationChange"
        />
      </b-tab-item>
      <b-tab-item
        headerClass="tabs--client-name"
        v-if="this.$route.params.clientName"
        :disabled="true"
      >
        <template slot="header">{{ this.$route.params.clientName }}</template>
      </b-tab-item>
    </b-tabs>

  </main>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import ClientForm from '@/components/client-form.vue'
import PreEnrollmentForm from '@/components/pre-enrollment-form/pre-enrollment-form.vue'
import PlanAffiliationsForm from '@/components/plan-affiliations-form/plan-affiliations-form.vue'

import AlertModal from '@/components/alert-modal.vue'
import Alert from '@/components/alert.vue'
import client from '@/api/networkConfigApi'
import { isSuccessResponse } from '@/helpers/is-success-response'
import { cloneDeep, get, isEqual } from 'lodash'
import store from '@/store/store'
import { createNewConfigArray } from '@/helpers/add-client-config'
import { obsIsEqual } from '@/helpers'
import planApi from '@/api/planApi'
import {
  buildNetworkNameOptions,
  buildClientsFromPlans,
  extractNetworkAffiliationUuids,
  resolveSaveEnabledState,
  buildAffiliationRequestBody,
  partitionPlansByNamespace,
  applyNetworkAffiliationUpdates,
  groupPlansByClientId,
  getPlansWithNetworkAffiliation,
  buildClientOptionsList,
  resolvePlanTypeSelection,
  buildSelectedClientFromId,
  applyNetworkAffiliationSelection,
  resolveClientAndPlanTypeFromPlans,
  buildInitiallySelectedClientSnapshot,
  buildEnableSavePlanAffiliationPayload
} from '@/utils/networkConfigUtils'

export default {
  name: 'client-config',
  components: {
    AlertModal,
    ClientForm,
    PreEnrollmentForm,
    Alert,
    PlanAffiliationsForm
  },
  data () {
    return {
      activeTab: 0,
      clientData: undefined,
      plans: [],

      plansInfo: [],
      clients: [],
      networkNames: [],
      draft: {
        name: '',
        networkConfigs: createNewConfigArray(),
        evenOptions: [],
        clientNameList: [],
        clientNameInvalid: false,
        loaded: false
      },
      isSaveNetworkModalOpen: false,
      isSaveModalOpen: false,
      isSavePlanAffiliationModalOpen: false,
      saveError: false,
      savedSuccess: false,
      unexpectedSaveError: false,
      saveEnabled: false,
      initialName: '',
      showDeepValidationErrors: false,
      clientConfigNetworkUuids: [],
      networksInvalid: false,
      preEnrollmentInvalid: false,
      preEnrollmentEnabled: false,
      reloadPlanAffilication: 0,
      loading: false,
      enabledPlanAffiliationTab: false,
      clientPlansList: [],
      planAffiliation: {
        planType: '',
        clientId: '',
        selectedClient: { title: 'NONE', value: '' },
        clientOptions: []
      },
      planAffiliationInitiallySelectedPlanType: '',
      planAffiliationInitiallySelectedClient: null,
      planAffiliationJvPlansGroupedByClientId: {},
      planAffiliationHrpPlansGroupedByClientId: {}
    }
  },
  computed: {
    ...mapGetters('networkConfig', [
      'getShowNetworkAlertModal',
      'getNetworkHasChanged'
    ]),
    planAffiliationNetworkUuids () {
      return (this.draft?.planAffiliations?.plans ?? [])
        .filter(p => p.networkAffiliation?.uuid)
        .map(p => p.networkAffiliation.uuid)
    }
  },
  methods: {
    ...mapActions([
      'networkConfig/setShowNetworkAlertModal',
      'networkConfig/setNetworkHasChanged'
    ]),
    setLoading (value) {
      this.loading = value
    },
    enableSavePlanAffiliation (planAffiliations) {
      this.saveEnabled = resolveSaveEnabledState(planAffiliations)
      if (this.saveEnabled) {
        this.draft.planAffiliations = planAffiliations
      }
    },
    handleWipePlanAffiliation () {
      this.draft.planAffiliations = { title: 'NONE' }
      this.$nextTick(() => {
        this.reloadPlanAffilication++
        this.initializePlanAffiliationTab()
      })
      this.$buefy.toast.open({
        message: 'Plan affiliations have been wiped!',
        type: 'is-info',
        duration: 5000,
        position: 'is-top'
      })
    },
    setSaveEnabled (formNoErrors) {
      const enablePreEnrollment = this.draft.networkConfigs.find((config) => {
        return config && config.includePreEnrollmentSite
      })
      this.preEnrollmentEnabled = !!enablePreEnrollment

      this.enabledPlanAffiliationTab = !!this.clientData

      const noChanges = obsIsEqual(this.draft, this.clientData ?? {})

      if (
        !this.draft?.name ||
        !this.draft?.networkConfigs[0]?.directoryType ||
        !formNoErrors
      ) {
        this.saveEnabled = false
      } else if (
        this.draft.networkConfigs ||
        this.draft.networkConfigs.length >= 1
      ) {
        this.saveEnabled = !noChanges
      }
    },
    async onClickSave () {
      const isPublished = get(this.clientData, 'preEnrollment.publish')

      if (this.activeTab === 0) {
        const include = this.draft.networkConfigs.some(
          (config) => config.includePreEnrollmentSite
        )

        if (!include) {
          this.savedSuccess = false
          this.saveError = false
          this.unexpectedSaveError = false
          this.isSaveNetworkModalOpen = true
          return
        }
      }

      if (this.activeTab === 1) {
        // if the site is already published and the user clicks on save
        if (!isPublished) {
          if (this.draft?.preEnrollment?.customContent) {
            this.createOrUpdateClient()
          } else {
            this.isSaveModalOpen = true
          }
        }
        return
      }

      this.createOrUpdateClient()
    },
    onAffiliationModalConfirm () {
      this.onSavePlanAffiliations(false)
      this.createOrUpdateClient()
      this.isSavePlanAffiliationModalOpen = false
    },
    async createOrUpdateClient (showAlert = true) {
      this.isSaveNetworkModalOpen = false
      this.isSaveModalOpen = false
      const data = this.draft
      data.name = data.name.trim()
      this.savedSuccess = false
      this.saveError = false
      this.unexpectedSaveError = false
      this.showDeepValidationErrors = false
      this.errorIndex = 0
      this.networksInvalid = false

      for (const config of data.networkConfigs) {
        if (config.isValid === false) {
          this.showDeepValidationErrors = true
          this.errorIndex = 0
          this.networksInvalid = true
          break
        }
        if (config.name) {
          config.name = config.name.trim()
        }

        if (config && config.directoryType === 'Centivo') {
          delete config.rbpAdministrator
          delete config.thirdPartyNetwork
          delete config.requireVpcModule
        } else if (
          config &&
          config.directoryType === 'Centivo + External/RBP'
        ) {
          delete config.requireVpcModule
        } else if (config && config.directoryType === 'External/RBP') {
          config.networkSeries = []
          delete config.preferredNetworkCodes
        }

        if (
          config &&
          (config.directoryType === 'Centivo' ||
            config.directoryType === 'Centivo + External/RBP')
        ) {
          config.preferredNetworkCodes = Array.isArray(config.preferredNetworkCodes)
            ? [...config.preferredNetworkCodes]
            : []
        }
      }

      const currentNetworkConfigs = data.networkConfigs

      const networkConfigByUuid = new Map(
        currentNetworkConfigs.map(config => [config.uuid, config])
      )

      for (const plan of data?.planAffiliations?.plans ?? []) {
        const config = networkConfigByUuid.get(plan.networkAffiliation.uuid)

        if (config && plan.networkAffiliation.networkName !== config.name) {
          plan.networkAffiliation.networkName = config.name
        }

        for (const option of plan.networkAffiliationOptions ?? []) {
          const optionConfig = networkConfigByUuid.get(option.value)

          if (optionConfig && option.title !== optionConfig.name) {
            option.title = optionConfig.name
          }
        }
      }

      if (this.networksInvalid || this.preEnrollmentInvalid) {
        this.saveError = true
        return
      }

      let response

      try {
        if (this.$route.params.clientName) {
          response = await client.updateClientConfig(
            this.clientData?.name,
            data
          )
        } else {
          response = await client.createClientConfig(data)
        }

        if (isSuccessResponse(response)) {
          await this.onSavePlanAffiliations()
          this['networkConfig/setNetworkHasChanged'](false)
          this.clientData = {
            ...response.data,
            planAffiliations: cloneDeep(this.draft.planAffiliations)
          }

          if (this.$route.params.clientName !== data.name) {
            this.$router.replace({
              name: 'ClientConfig',
              params: { clientName: data.name },
              silent: true
            })
          }

          if (showAlert) {
            this.savedSuccess = true
          }
        } else {
          console.error(response.data)
          this.unexpectedSaveError = true
        }
      } catch (e) {
        console.error(e)
        this.unexpectedSaveError = true
      } finally {
        this.setLoading(false)
      }

      this.isSaveModalOpen = false

      return response
    },
    getNetworkNames () {
      const networkNames = buildNetworkNameOptions(this.draft?.networkConfigs)

      this.networkNames = networkNames

      return networkNames
    },
    getClients (plans = [], networkNames) {
      return buildClientsFromPlans(plans, networkNames, this.$route.params.clientName)
    },
    async onSavePlanAffiliations (confirmAction = true) {
      if (
        confirmAction &&
        this.clientPlansList?.some((p) => !p.networkAffiliation)
      ) {
        this.isSavePlanAffiliationModalOpen = true

        return false
      }
      this.setLoading(true)
      const changeAffiliationRequestBody = buildAffiliationRequestBody(
        this.draft?.planAffiliations,
        this.draft?.name
      )

      try {
        const res = await planApi.updatePlanNetworkAffiliationForClient(
          changeAffiliationRequestBody
        )
        const data = res.data
        // update store without refetching all plans
        const plansToUpdateNetworkAffilication = [...data.added, ...data.removed]
        if (plansToUpdateNetworkAffilication.length) {
          const { jvPlans: jvPlansToUpdateNetworkAffiliation, hrpPlans: hrpPlansToUpdateNetworkAffiliation } =
            partitionPlansByNamespace(plansToUpdateNetworkAffilication)

          if (jvPlansToUpdateNetworkAffiliation.length) {
            const jvPlans = store.state.plans.jvPlans
            await store.dispatch('plans/updateJvPlans', applyNetworkAffiliationUpdates(jvPlans, jvPlansToUpdateNetworkAffiliation))
          }

          if (hrpPlansToUpdateNetworkAffiliation.length) {
            const hrpPlans = store.state.plans.hrpPlans
            await store.dispatch('plans/updateHrpPlans', applyNetworkAffiliationUpdates(hrpPlans, hrpPlansToUpdateNetworkAffiliation))
          }
        }

        const networkNames = this.getNetworkNames()

        this.setPlanNetworkConfigs(this.plansInfo)
        this.clients = this.getClients(this.plansInfo, networkNames)

        this['networkConfig/setNetworkHasChanged'](false)
        this.savedSuccess = true
      } catch (error) {
        console.log(error)
        this.saveError = true
      } finally {
        this.isSavePlanAffiliationModalOpen = false
      }
    },
    onKeepWorking () {
      this['networkConfig/setShowNetworkAlertModal'](false)
      this.isSavePlanAffiliationModalOpen = false
    },
    async submitPreEnrollment (data) {
      if (!isEqual(this.draft, this.clientData)) {
        const response = await this.createOrUpdateClient(false)

        if (!response || !isSuccessResponse(response)) {
          return
        }
      } else {
        this.isSaveModalOpen = false
      }

      const isPublished = get(this.clientData, 'preEnrollment.publish')

      let response
      if (isPublished) {
        response = await client.unpublishOESite(this.clientData.name)
      } else {
        response = await client.publishOESite(this.clientData.name)
      }

      if (isSuccessResponse(response)) {
        this.clientData = response.data
      }

      return response
    },
    goHome () {
      const toPath = this.getShowNetworkAlertModal || '/plans'

      this['networkConfig/setNetworkHasChanged'](false)
      this.$router.push({ path: toPath }).catch(() => {})
    },
    keepChanges () {
      this['networkConfig/setShowNetworkAlertModal'](false)
    },
    setPlanNetworkConfigs (plans) {
      this.clientConfigNetworkUuids.push(...extractNetworkAffiliationUuids(plans))
    },
    hideValidationErrors () {
      this.showDeepValidationErrors = false
    },
    setPreEnrollmentInvalid (invalid) {
      this.preEnrollmentInvalid = invalid
    },
    initializePlanAffiliationTab () {
      this.planAffiliationJvPlansGroupedByClientId = groupPlansByClientId(store.state.plans.jvPlans)
      this.planAffiliationHrpPlansGroupedByClientId = groupPlansByClientId(store.state.plans.hrpPlans)
      this.planAffiliation = {
        planType: '',
        clientId: '',
        selectedClient: { title: 'NONE', value: '' },
        clientOptions: []
      }
      this.planAffiliationInitiallySelectedPlanType = ''
      this.planAffiliationInitiallySelectedClient = null
      this.refreshPlanAffiliationClientOptions()
    },
    refreshPlanAffiliationClientOptions () {
      this.planAffiliation.clientOptions = buildClientOptionsList(
        this.planAffiliation.planType === 'HRP'
          ? this.planAffiliationHrpPlansGroupedByClientId
          : this.planAffiliationJvPlansGroupedByClientId,
        this.networkNames,
        this.$route.params.clientName,
        this.planAffiliation.clientId
      )

      if (this.planAffiliation.clientId && this.planAffiliation.clientId !== 'NONE') {
        this.planAffiliation.selectedClient = buildSelectedClientFromId(
          this.planAffiliation.clientId,
          this.planAffiliation.clientOptions
        )
      } else {
        this.planAffiliation.selectedClient = { title: 'NONE', value: '' }
      }
    },
    bootstrapPlanAffiliationFromSavedPlans () {
      if (this.planAffiliationInitiallySelectedPlanType) return

      const affiliatedPlans = getPlansWithNetworkAffiliation(
        store.state.plans.jvPlans,
        store.state.plans.hrpPlans,
        this.networkNames
      )
      const derivedSelection = resolveClientAndPlanTypeFromPlans(affiliatedPlans)
      if (!derivedSelection) return

      this.planAffiliation.clientId = derivedSelection.clientId
      this.planAffiliation.planType = derivedSelection.planType
      this.planAffiliationInitiallySelectedPlanType = derivedSelection.planType
      this.planAffiliationInitiallySelectedClient = buildInitiallySelectedClientSnapshot(
        derivedSelection.clientId,
        affiliatedPlans
      )
      this.refreshPlanAffiliationClientOptions()
    },
    onPlanAffiliationPlanTypeChange (planType) {
      const { clientId } = resolvePlanTypeSelection(
        planType,
        this.planAffiliationInitiallySelectedPlanType,
        this.planAffiliationInitiallySelectedClient
      )
      this.planAffiliation.planType = planType
      this.planAffiliation.clientId = clientId
      this.refreshPlanAffiliationClientOptions()
    },
    onPlanAffiliationClientIdChange (e) {
      this.planAffiliation.clientId = e.target.value
      this.refreshPlanAffiliationClientOptions()
      this.syncPlanAffiliationSaveState()
    },
    onPlanAffiliationNetworkAffiliationChange (e) {
      const planId = e.target.name
      const networkUuid = e.target.value
      this.planAffiliation.selectedClient = {
        ...this.planAffiliation.selectedClient,
        plans: applyNetworkAffiliationSelection(
          this.planAffiliation.selectedClient.plans,
          planId,
          networkUuid,
          this.$route.params.clientName
        )
      }
      this.syncPlanAffiliationSaveState()
    },
    syncPlanAffiliationSaveState () {
      this.enableSavePlanAffiliation(
        buildEnableSavePlanAffiliationPayload(
          this.planAffiliation.selectedClient,
          this.planAffiliation.planType
        )
      )
    }
  },
  async created () {
    if (!store.state.plans.jvPlans?.length || !store.state.plans.hrpPlans?.length) {
      await store.dispatch('plans/getAllPlans')
    }

    this.setLoading(true)

    if (this.$route.params.clientName) {
      await store.dispatch('networkConfig/getClientConfigs')

      this.isEdit = true
      const response = await client.getClient(this.$route.params.clientName)

      if (isSuccessResponse(response)) {
        const data = {
          evenOptions: [],
          ...response.data
        }

        if (!response.data.planAffiliations) {
          const selectedClient = this.clients.find((c) => c.isSelected)

          const isHrpPlanType = selectedClient?.plans.some((plan) =>
            plan?.planId?.startsWith('HRP_')
          )
          this.clientPlansList = selectedClient?.plans
          data.planAffiliations = {
            // TOOD make planAffiliations if not in data
            planType: isHrpPlanType
              ? 'HRP'
              : selectedClient?.plans
                ? 'Javelina'
                : ''
          }
        }

        if (!data.networkConfigs || data.networkConfigs.length < 1) {
          data.networkConfigs = createNewConfigArray()
        } else {
          for (const config of data.networkConfigs) {
            if (
              config &&
              config.directoryType === 'External/RBP' &&
              config.thirdPartyNetwork !== 'None' &&
              !config.requireVpcModule
            ) {
              config.requireVpcModule = false
            }
          }
        }
        this.clientData = data
        this.draft = cloneDeep(data)
        this.initialName = data.name ? data.name.toLowerCase() : ''
      }
    }

    this.getNetworkNames()
    this.initializePlanAffiliationTab()
    this.setPlanNetworkConfigs(this.plansInfo)
    this.clients = this.getClients(this.plansInfo, this.networkNames)

    this.reloadPlanAffilication++
    this.bootstrapPlanAffiliationFromSavedPlans()
    this.setLoading(false)
  },
  watch: {
    clientData: {
      handler: function (newVal) {
        this.draft = cloneDeep(newVal)
        const networkNames = this.getNetworkNames()

        if (networkNames.length > 0 && this.plansInfo.length > 0) {
          this.clients = this.getClients(this.plansInfo, networkNames)
        }

        this.bootstrapPlanAffiliationFromSavedPlans()
        this['networkConfig/setNetworkHasChanged'](false)
      },
      deep: true
    },
    draft: {
      handler: function (newVal) {
        this['networkConfig/setNetworkHasChanged'](
          !isEqual(newVal, this.clientData)
        )
      },
      deep: true
    },
    'draft.networkConfigs': {
      handler () {
        this.getNetworkNames()
        this.refreshPlanAffiliationClientOptions()
      },
      deep: true
    },
    activeTab: function () {
      this.saveError = false
      this.unexpectedSaveError = false
      this.savedSuccess = false
    }
  },
  beforeDestroy () {
    this['networkConfig/setShowNetworkAlertModal'](false)
    this['networkConfig/setNetworkHasChanged'](false)
  }
}
</script>

<style lang="scss">
main.network--content.container {
  padding-top: 0;
}

.tabs--client-name {
  opacity: 1 !important;

  a {
    line-height: 1rem;
    border: none;
    padding-left: 2rem;
    font-weight: 600;
    font-size: 1.25rem;
    color: #444;
  }
}

.network--tabs {
  .tabs ul {
    padding-top: 5.9rem;
  }

  .tabs li:not(.tabs--client-name) {
    border: 1px solid #00a69e;
    border-bottom: none;
    width: 9.25rem;

    a,
    a:hover {
      font-weight: 700;
      color: #00a69e;
      border-bottom: none;
    }

    &:not(:last-child) {
      margin-right: 0.125rem;
    }

    &.is-active {
      background-color: #00a69e;

      a {
        color: #fff;
      }
    }

    &.is-disabled {
      border: none;
      background-color: #ebebeb;
      cursor: not-allowed;
      opacity: 1 !important;

      a {
        pointer-events: all;
        color: #bbbdbf;
      }
      a:active {
        pointer-events: none;
      }
    }

    .tooltip-content {
      width: 20rem;
      white-space: pre-wrap;
      margin-bottom: 0.5rem;
      font-weight: 600;
      text-align: left;
      border-radius: 2px;
      padding: 0.5rem;
    }
  }

  .networks-tooltip {
    .tooltip-content {
      margin-left: 85px;
    }
  }
}

.config--save-container {
  position: absolute;
  right: 0;
  top: 6rem;
  transform: translateY(-50%);
  display: flex;

  > button {
    background-color: #ec008c;
    color: #fff;
    font-weight: 700;
    padding: 1rem 2.5rem;
    width: 7rem;
    height: 3rem;

    &:disabled,
    &:hover,
    &:active,
    &:focus {
      border-color: #ec008c;
      color: #ec008c;
      background-color: #ec008c;
      color: #fff;
    }
  }

  .submit--alert {
    margin-top: 2.5rem;
  }

  .save--alert {
    margin-right: 2.5rem;

    .error--bold {
      font-weight: 700;
    }
  }
}
</style>
